import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "editable",
    "toggle",
    "layout",
    "railContent",
    "collapseButton",
    "collapseIcon",
    "generatedEditor",
    "previewForm",
    "previewButton",
    "railOverlay",
    "comparisonRows",
    "recentRuns",
    "validationWarnings",
    "fixedActions",
    "languageSelect",
    "articleId",
    "languageArticleId"
  ]

  connect() {
    this.positionRailOverlay = this.positionRailOverlay.bind(this);
    this.dragActualTranslationPopover = this.dragActualTranslationPopover.bind(this);
    this.stopActualTranslationDrag = this.stopActualTranslationDrag.bind(this);
    this.hasUnsavedChanges = false;
    this.previewSnapshot = this.currentPreviewSnapshot();
  }

  disconnect() {
    this.stopActualTranslationDrag();
  }

  toggle(event) {
    const button = event.currentTarget;
    const targetId = button.dataset.targetId;
    const field = this.editableTargets.find((element) => element.dataset.targetId === targetId);
    if (!field) return;

    const isReadOnly = field.hasAttribute("readonly");
    if (isReadOnly) {
      field.removeAttribute("readonly");
      field.focus();
      button.textContent = "Lock";
      button.classList.add("translation-ui__button--accent");
    } else {
      field.setAttribute("readonly", "readonly");
      button.textContent = "Edit";
      button.classList.remove("translation-ui__button--accent");
    }
  }

  markUnsaved(event) {
    if (event.type === "change" && this.hasLanguageSelectTarget && event.target === this.languageSelectTarget) return;

    this.hasUnsavedChanges = true;
  }

  changeTargetLanguage(event) {
    const select = event.currentTarget;
    const previousValue = select.dataset.previousValue || "";

    if (this.hasUnsavedWork() && !window.confirm("You have unsaved changes. Are you sure you want to continue?")) {
      select.value = previousValue;
      return;
    }

    select.dataset.previousValue = select.value;
    if (this.hasArticleIdTarget && this.hasLanguageArticleIdTarget) {
      this.languageArticleIdTarget.value = this.articleIdTarget.value;
    }
    select.form.requestSubmit();
  }

  hasUnsavedWork() {
    return this.hasUnsavedChanges || this.currentPreviewSnapshot() !== this.previewSnapshot;
  }

  currentPreviewSnapshot() {
    if (!this.hasPreviewFormTarget) return "";

    const entries = [];
    const formData = new FormData(this.previewFormTarget);
    formData.forEach((value, key) => {
      if (key !== "authenticity_token" && key !== "utf8") {
        entries.push([key, value]);
      }
    });

    this.generatedEditorTargets.forEach((editorField) => {
      const payloadName = editorField.dataset.payloadName || editorField.id;
      if (!payloadName) return;

      const editor = window.tinymce && editorField.id ? window.tinymce.get(editorField.id) : null;
      entries.push([payloadName, editor ? editor.getContent() : editorField.innerHTML]);
    });

    return JSON.stringify(entries);
  }

  toggleSidebar() {
    if (!this.hasLayoutTarget || !this.hasRailContentTarget || !this.hasCollapseButtonTarget) return;

    const collapsedClass = "translation-playground--rail-collapsed";
    const containerCollapsedClass = "translation-ui__container--rail-collapsed";
    const isCollapsed = this.layoutTarget.classList.toggle(collapsedClass);
    this.element.classList.toggle(containerCollapsedClass, isCollapsed);

    this.railContentTarget.classList.toggle("translation-ui__hidden", isCollapsed);
    if (this.hasCollapseIconTarget) {
      this.collapseIconTarget.textContent = isCollapsed ? "‹" : "›";
    }
    this.collapseButtonTarget.setAttribute("aria-label", isCollapsed ? "Expand sidebar" : "Collapse sidebar");
    this.collapseButtonTarget.setAttribute("title", isCollapsed ? "Expand sidebar" : "Collapse sidebar");
    this.collapseButtonTarget.setAttribute("aria-expanded", String(!isCollapsed));
  }

  syncGeneratedContent(event) {
    this.collectGeneratedContent(event.currentTarget);
  }

  collectGeneratedContent(form) {
    if (!this.hasGeneratedEditorTarget) return;

    if (window.tinymce && typeof window.tinymce.triggerSave === "function") {
      window.tinymce.triggerSave();
    }

    this.generatedEditorTargets.forEach((editorField) => {
      const payloadName = editorField.dataset.payloadName;
      if (!payloadName) return;

      const editor = window.tinymce && editorField.id ? window.tinymce.get(editorField.id) : null;
      if (editor && typeof editor.save === "function") {
        editor.save();
      }

      const content = editor ? editor.getContent() : editorField.innerHTML;
      this.upsertHiddenField(form, payloadName, content);
    });
  }

  upsertHiddenField(form, name, value) {
    Array.from(form.querySelectorAll('input[type="hidden"]'))
      .filter((field) => field.name === name)
      .forEach((field) => field.remove());

    const hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = name;
    hiddenInput.value = value || "";
    form.appendChild(hiddenInput);
  }

  runPreview(event) {
    if (event.target !== event.currentTarget) return;

    event.preventDefault();

    const form = event.currentTarget;
    this.collectGeneratedContent(form);
    const formData = new FormData(form);

    this.setPreviewLoading(true);

    fetch(form.action, {
      method: form.method.toUpperCase(),
      body: formData,
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    })
      .then((response) => response.json().then((payload) => ({ response, payload })))
      .then(({ response, payload }) => {
        if (!response.ok) {
          throw new Error(payload.error || "Playground preview failed.");
        }

        this.replacePreviewContent(payload);
        this.updateBrowserUrl(payload);
        this.hasUnsavedChanges = false;
        this.previewSnapshot = this.currentPreviewSnapshot();
      })
      .catch((error) => {
        window.alert(error.message || "Playground preview failed.");
      })
      .finally(() => {
        this.setPreviewLoading(false);
      });
  }

  replacePreviewContent(payload) {
    this.removeGeneratedEditors();

    if (this.hasComparisonRowsTarget) {
      this.comparisonRowsTarget.innerHTML = payload.comparison_rows_html || "";
    }
    if (this.hasRecentRunsTarget) {
      this.recentRunsTarget.innerHTML = payload.recent_runs_html || "";
    }
    if (this.hasValidationWarningsTarget) {
      this.validationWarningsTarget.innerHTML = payload.validation_warnings_html || "";
    }
    if (this.hasFixedActionsTarget) {
      this.fixedActionsTarget.innerHTML = payload.fixed_actions_html || "";
    }

    this.initializeGeneratedEditors();
  }

  removeGeneratedEditors() {
    if (!window.tinymce) return;

    this.generatedEditorTargets.forEach((editorField) => {
      const editor = editorField.id ? window.tinymce.get(editorField.id) : null;
      if (editor && typeof editor.remove === "function") {
        editor.remove();
      }
    });
  }

  initializeGeneratedEditors() {
    if (!window.tinymce || typeof window.tinymce.init !== "function") return;

    window.tinymce.init(this.tinyMceOptions());
  }

  tinyMceOptions() {
    return {
      selector: ".tinymce-content-editable",
      menubar: false,
      inline: true,
      plugins: [
        "link",
        "lists",
        "powerpaste",
        "autolink",
        "tinymcespellchecker",
        "code"
      ],
      toolbar: [
        "code | removeformat | undo redo | bold italic underline | forecolor backcolor",
        "alignleft aligncenter alignright alignfull | numlist bullist outdent indent blockquote"
      ],
      invalid_elements: "div,span",
      valid_styles: {
        "*": "font-size,font-family,font-weight,font-style,color,text-decoration,text-align"
      },
      powerpaste_word_import: "clean",
      powerpaste_html_import: "clean",
      default_link_target: "_blank"
    };
  }

  updateBrowserUrl(payload) {
    if (!payload.run_id || !payload.article_id) return;

    const url = new URL(window.location.href);
    url.searchParams.set("run_id", payload.run_id);
    url.searchParams.set("article_id", payload.article_id);
    if (payload.target_lang) {
      url.searchParams.set("target_lang", payload.target_lang);
    }
    window.history.pushState({}, "", url.toString());
  }

  setPreviewLoading(isLoading) {
    this.setRailButtonsDisabled(isLoading);

    if (this.hasRailOverlayTarget) {
      this.railOverlayTarget.hidden = !isLoading;
      if (isLoading) {
        this.positionRailOverlay();
        window.addEventListener("resize", this.positionRailOverlay);
        window.addEventListener("scroll", this.positionRailOverlay, true);
      } else {
        window.removeEventListener("resize", this.positionRailOverlay);
        window.removeEventListener("scroll", this.positionRailOverlay, true);
      }
    }
    const rail = this.hasRailContentTarget ? this.railContentTarget.closest(".translation-playground__rail") : null;
    if (rail) {
      rail.classList.toggle("is-loading", isLoading);
      rail.setAttribute("aria-busy", String(isLoading));
    }
  }

  setRailButtonsDisabled(isDisabled) {
    const rail = this.hasRailContentTarget ? this.railContentTarget.closest(".translation-playground__rail") : null;
    if (!rail) return;

    const buttons = rail.querySelectorAll("button, input[type='submit']");
    buttons.forEach((button) => {
      if (isDisabled) {
        if (button.disabled) return;

        button.dataset.translationPlaygroundDisabled = "true";
        button.disabled = true;
      } else if (button.dataset.translationPlaygroundDisabled === "true") {
        button.disabled = false;
        delete button.dataset.translationPlaygroundDisabled;
      }
    });
  }

  startActualTranslationDrag(event) {
    if (event.button !== 0 || event.target.closest("button")) return;

    const popover = event.currentTarget.closest(".translation-compare__actual-popover");
    if (!popover) return;

    event.preventDefault();
    const rect = popover.getBoundingClientRect();
    this.actualTranslationDrag = {
      popover,
      pointerOffsetX: event.clientX - rect.left,
      pointerOffsetY: event.clientY - rect.top
    };

    popover.style.margin = "0";
    popover.style.inset = "auto";
    popover.style.left = `${rect.left}px`;
    popover.style.top = `${rect.top}px`;

    document.addEventListener("pointermove", this.dragActualTranslationPopover);
    document.addEventListener("pointerup", this.stopActualTranslationDrag);
    document.addEventListener("pointercancel", this.stopActualTranslationDrag);
  }

  dragActualTranslationPopover(event) {
    if (!this.actualTranslationDrag) return;

    event.preventDefault();
    const { popover, pointerOffsetX, pointerOffsetY } = this.actualTranslationDrag;
    const rect = popover.getBoundingClientRect();
    const maxLeft = Math.max(0, window.innerWidth - rect.width);
    const maxTop = Math.max(0, window.innerHeight - rect.height);
    const nextLeft = Math.min(Math.max(0, event.clientX - pointerOffsetX), maxLeft);
    const nextTop = Math.min(Math.max(0, event.clientY - pointerOffsetY), maxTop);

    popover.style.left = `${nextLeft}px`;
    popover.style.top = `${nextTop}px`;
  }

  stopActualTranslationDrag() {
    this.actualTranslationDrag = null;
    document.removeEventListener("pointermove", this.dragActualTranslationPopover);
    document.removeEventListener("pointerup", this.stopActualTranslationDrag);
    document.removeEventListener("pointercancel", this.stopActualTranslationDrag);
  }

  positionRailOverlay() {
    if (!this.hasRailOverlayTarget || !this.hasRailContentTarget) return;

    const rail = this.railContentTarget.closest(".translation-playground__rail");
    if (!rail) return;

    const rect = rail.getBoundingClientRect();
    this.railOverlayTarget.style.left = `${rect.left}px`;
    this.railOverlayTarget.style.top = `${rect.top}px`;
    this.railOverlayTarget.style.width = `${rect.width}px`;
    this.railOverlayTarget.style.height = `${rect.height}px`;
  }

}
